import { startOfDay } from "date-fns";
import { twJoin } from "tailwind-merge";

import { Button } from "@/design-system/Button";
import { getDateFromMinutesOfDay, SQL_SERVER_MIN_DATE } from "@/helpers/date";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useOnScroll } from "@/hooks/useOnScroll";
import { useTranslation } from "@/translations";
import type { PickerProps, PickerRef } from "@/ui-adapters/AntDatePicker";

import { CalendarIcon } from "../Icons/CalendarIcon";
import { disabledTime } from "./disabled-time";
import { AntDatePicker } from "./picker";
import { connectedPickerPlacements } from "./placements";
import { useConnectedPickerPlacement } from "./useConnectedPickerPlacement";

export interface DatePickerProps extends React.RefAttributes<PickerRef> {
  id?: string;
  name?: string;
  placeholder?: string;
  value: Date | undefined;
  defaultValue?: Date;
  onChange: (value: Date | undefined) => void;
  min?: Date;
  max?: Date;
  disabledDate?: (current: Date) => boolean;
  type?: "date" | "datetime" | "time";
  minuteStep?: PickerProps<Date>["minuteStep"];
  enableMidnight?: boolean;
  showNow?: boolean;
  allowClear?: boolean;
  error?: boolean;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "data-testid"?: string;
  ref?: React.Ref<PickerRef>;
  disabled?: boolean;
}

export function DatePicker({
  value,
  defaultValue,
  onChange,
  min,
  max,
  disabledDate,
  type = "date",
  minuteStep,
  enableMidnight = false,
  showNow = true,
  allowClear = false,
  error,
  ref,
  ...props
}: DatePickerProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const [isOpened, openHandler] = useBool(false);
  const connectedPickerPlacement = useConnectedPickerPlacement(isOpened);

  const { t } = useTranslation();

  useOnScroll(() => {
    openHandler.setFalse();
  });

  let format = "DD/MM/YYYY HH:mm";
  if (type === "time") {
    format = "HH:mm";
  } else if (type === "date") {
    format = "DD/MM/YYYY";
  }

  const isDateDisabled = (date: Date) => {
    const effectiveMin = min ?? SQL_SERVER_MIN_DATE;
    if (date < startOfDay(effectiveMin)) {
      return true;
    }

    // Max is inclusive
    if (max && date >= max) {
      return true;
    }

    if (disabledDate) {
      return disabledDate(date);
    }

    return false;
  };

  const handleChange = (date: Date | Date[]) => {
    if (date instanceof Date) {
      onChange(isDateDisabled(date) ? undefined : date);
    } else {
      onChange(undefined);
    }
  };

  const handleConfirmSelection = () => {
    openHandler.setFalse();
  };

  const todayDate = clockDate;
  const shouldShowNowButton = showNow && !isDateDisabled(todayDate);

  return (
    <AntDatePicker
      {...props}
      ref={ref}
      picker={type === "time" ? "time" : "date"}
      onCalendarChange={handleChange}
      format={{
        format,
        type: "mask",
      }}
      open={isOpened}
      onOpenChange={openHandler.set}
      builtinPlacements={connectedPickerPlacements}
      classNames={{ popup: { root: connectedPickerPlacement.popupClassName } }}
      panelRender={(panel) => <div data-portal-dropdown="true">{panel}</div>}
      showTime={
        type === "datetime" || type === "time" ? { format: "HH:mm", defaultOpenValue: defaultValue || min } : undefined
      }
      className={twJoin("w-select-responsive-filter max-w-full", connectedPickerPlacement.inputClassName)}
      disabledDate={isDateDisabled}
      disabledTime={(current: Date) => disabledTime(enableMidnight, current, min, max)}
      minuteStep={minuteStep}
      value={typeof value === "number" ? getDateFromMinutesOfDay(value) : value}
      defaultValue={defaultValue}
      minDate={min}
      maxDate={max}
      showNow={false}
      renderExtraFooter={() => (
        <footer className="flex justify-end gap-space-md py-space-sm">
          {shouldShowNowButton && (
            <Button
              variant="ghostPrimary"
              size="sm"
              data-testid="date-picker-now-button"
              onClick={() => handleChange(new Date())}
            >
              {t("component.date-picker.btn.now")}
            </Button>
          )}
          <Button size="sm" onClick={handleConfirmSelection}>
            {t("common.action.ok")}
          </Button>
        </footer>
      )}
      allowClear={allowClear}
      needConfirm={false}
      status={error ? "error" : undefined}
      size="large"
      suffixIcon={
        <span className="inline-flex transition-colors">
          <CalendarIcon size={16} tone={props.disabled ? "disabled" : "secondary"} />
        </span>
      }
    />
  );
}
